import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { useDebounce } from '@/shared/hooks/useDebounce';

export interface RegisterSession { _id: string; cashierId: any; status: string; cashInHand: number; openedAt: string; closedAt?: string; totalSales: number; saleCount: number; countedCash?: number; expectedCash?: number; variance?: number | null; varianceExplanation?: string; varianceApprovedBy?: any; }
export interface PosSale { _id: string; saleNumber: string; customerName: string; netTotal: number; tenders: any[]; changeGiven: number; createdAt: string; }
export interface SuspendedCart { _id: string; customerName: string; lines: any[]; subTotal: number; suspendedAt: string; }

export function useMySession() { return useQuery({ queryKey: ['pos', 'my-session'], queryFn: async () => { const { data } = await api.get('/api/pos/registers/my-session'); return data.session as RegisterSession | null; } }); }
export function useRecentSales() { return useQuery({ queryKey: ['pos', 'recent-sales'], queryFn: async () => { const { data } = await api.get('/api/pos/checkout/recent'); return data.sales as PosSale[]; } }); }
export function useSuspendedCarts() { return useQuery({ queryKey: ['pos', 'carts'], queryFn: async () => { const { data } = await api.get('/api/pos/carts'); return data as SuspendedCart[]; } }); }

export function useOpenRegister() { const qc = useQueryClient(); return useMutation({ mutationFn: async (payload: { cashInHand: number; locationId?: string }) => { const { data } = await api.post('/api/pos/registers/open', payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['pos'] }) }); }
export function useCloseRegister() { const qc = useQueryClient(); return useMutation({ mutationFn: async (payload: { countedCash: number; notes?: string }) => { const { data } = await api.post('/api/pos/registers/close', payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['pos'] }) }); }
export function useCheckout() { const qc = useQueryClient(); return useMutation({ mutationFn: async (payload: any) => { const { data } = await api.post('/api/pos/checkout', payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['pos'] }) }); }
export function useSuspendCart() { const qc = useQueryClient(); return useMutation({ mutationFn: async (payload: any) => { const { data } = await api.post('/api/pos/carts', payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['pos', 'carts'] }) }); }
export function useResumeCart() { const qc = useQueryClient(); return useMutation({ mutationFn: async (id: string) => { const { data } = await api.post(`/api/pos/carts/${id}/resume`); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['pos', 'carts'] }) }); }
// Checklist item 68 ("In Add products the module refreshes after each Alphabet").
// The search fired on every keystroke, so typing "R" through "RRM-15W" hammered
// /api/catalog/products/search once per letter and the dropdown kept reordering
// under the cursor. The debounce lives HERE rather than in each caller, so the
// quotation, the sales-order builder and the POS screen all stop re-firing per
// character instead of one of them being fixed and the rest left as they were.
export function useProductSearch(q: string) {
  const term = useDebounce(String(q ?? '').trim(), 300);
  const isDebouncing = String(q ?? '').trim() !== term;
  const query = useQuery({
    queryKey: ['catalog', 'search', term],
    queryFn: async () => {
      if (!term) return [];
      const { data } = await api.get(`/api/catalog/products/search?q=${encodeURIComponent(term)}`);
      return data as any[];
    },
    enabled: term.length >= 1,
    placeholderData: (previousData) => previousData,
  });

  return {
    ...query,
    isLoading: query.isLoading || (isDebouncing && String(q ?? '').trim().length >= 1),
    isDebouncing,
  };
}


// ── Void a POS sale ──────────────────────────────────────────────────────────

export function useVoidSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ saleId, reason }: { saleId: string; reason: string }) => {
      const { data } = await api.post(`/api/pos/checkout/${saleId}/void`, { reason });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pos'] }),
  });
}

// ── Register session variance approval (admin) ──────────────────────────────

export function useClosedRegisterSessions() {
  return useQuery({
    queryKey: ['pos', 'registers', 'closed'],
    queryFn: async () => {
      const { data } = await api.get('/api/pos/registers', { params: { status: 'closed', limit: 50 } });
      return (data.data as RegisterSession[]).filter((s) => s.variance != null && s.variance !== 0 && !s.varianceApprovedBy);
    },
  });
}

export function useApproveVariance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, explanation }: { sessionId: string; explanation: string }) => {
      const { data } = await api.post(`/api/pos/registers/${sessionId}/approve-variance`, { explanation });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pos', 'registers'] }),
  });
}
